import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../services/api';
import owlLogo from '../Assets/logo icon transparent background.png';
import { Search, MapPin, Phone, Globe, Clock, Edit3, Building, Edit, Eye, Star, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';

const BusinessSetup = () => {
  const { user } = useAuth();
  const { setCurrentBusiness } = useBusiness();
  const [setupStep, setSetupStep] = useState('search'); // 'search', 'details', 'scraping', 'assistant'
  const [searchMode, setSearchMode] = useState('auto'); // 'auto' or 'manual'
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [scraping, setScraping] = useState(false); // Add scraping state
  const [scrapingComplete, setScrapingComplete] = useState(false); // Add scraping complete state
  const [scrapingStep, setScrapingStep] = useState(0);
  const [scrapingStatus, setScrapingStatus] = useState('');
  const scrapingSteps = [
    "Connecting to website...",
    "Crawling pages...",
    "Extracting content...",
    "Structuring content for knowledge base...",
    "Finalizing and saving data..."
  ];
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  // Google Places data
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);

  // Store the complete place details when we fetch them
  const [placeDetailsData, setPlaceDetailsData] = useState(null);

  // Add new state for Google Places review interface
  const [showGooglePlacesReview, setShowGooglePlacesReview] = useState(false);
  const [editableGoogleData, setEditableGoogleData] = useState(null);
  const [isEditingGoogleData, setIsEditingGoogleData] = useState(false);

  // Add new state for website content review
  const [showWebsiteReview, setShowWebsiteReview] = useState(false);
  const [editableWebsiteData, setEditableWebsiteData] = useState(null);
  const [isEditingWebsiteData, setIsEditingWebsiteData] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    // Business details
    name: '',
    address: '',
    placeId: '',
    phone: '',
    website: '',
    category: '',
    primaryType: '',
    latitude: null,
    longitude: null,
    openingHours: [],
    
    // Assistant details
    assistantName: '',
    businessType: '',
    customType: '',
    description: '',
  });

  // Business categories for manual entry
  const businessCategories = [
    'Restaurant', 'Retail Store', 'Salon & Spa', 'Healthcare', 'Legal Services',
    'Real Estate', 'Technology', 'Education', 'Finance & Banking', 'Automotive',
    'Home Services', 'Professional Services', 'Entertainment', 'Travel & Tourism',
    'Sports & Recreation', 'Beauty & Personal Care', 'Food & Beverage',
    'Consulting', 'Marketing & Advertising', 'Construction', 'Other'
  ];

  useEffect(() => {
    checkExistingBusiness();
  }, [user]);

  // Update handlePlaceSearch to use backend API
  const handlePlaceSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    setError('');
    setSearchResults([]);

    try {
      console.log('Searching for:', searchQuery);
      const response = await api.googlePlaces.search(searchQuery);
      console.log('Search response:', response);
      
      if (response.data && response.data.results && response.data.results.length > 0) {
        setSearchResults(response.data.results);
        // Don't change step here - show results in the same search step
      } else {
        setError('No businesses found. Please try a different search term or use manual entry.');
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setError('Failed to search for businesses. Please try manual entry.');
    } finally {
      setSearching(false);
    }
  };

  const handlePlaceSelection = async (place) => {
    try {
      setError('');
      setSearching(true);
      console.log('Getting details for place:', place);
      const response = await api.googlePlaces.getDetails(place.place_id);
      console.log('Place details response:', response);
      
      if (response.data) {
        const placeDetails = response.data;
        // Store the complete details for later use
        setPlaceDetailsData(placeDetails);
        populateFormFromPlace(placeDetails);
        setSetupStep('details'); // Move to details step after getting place details
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      setError('Failed to get business details. Please try manual entry.');
    } finally {
      setSearching(false);
    }
  };

  const populateFormFromPlace = (place) => {
    const primaryType = place.types?.[0] || '';
    // Use the primary_category directly from Google Places API
    const category = place.primary_category || 'Other';

    // Extract coordinates from geometry
    const latitude = place.geometry?.location?.lat || null;
    const longitude = place.geometry?.location?.lng || null;

    setFormData(prev => ({
      ...prev,
      name: place.name || '',
      address: place.formatted_address || '',
      placeId: place.place_id || '',
      phone: place.formatted_phone_number || '',
      website: place.website || '',
      category: category, // This will be the Google Places category
      primaryType: primaryType,
      latitude: latitude,
      longitude: longitude,
      openingHours: place.opening_hours?.weekday_text || [],
      assistantName: place.name ? `${place.name} Assistant` : ''
    }));
  };

  const checkExistingBusiness = async () => {
    if (!user?.email) {
      setChecking(false);
      return;
    }
    
    try {
      const response = await api.getBusinessByEmail(user.email);
      if (response.data) {
        console.log('Found existing business:', response.data);
        setCurrentBusiness(response.data);
        return;
      }
      console.log('No existing business found');
      setChecking(false);
    } catch (error) {
      console.error('Error checking business:', error);
      setError('Error checking business status. Please refresh the page.');
      setChecking(false);
    }
  };

  const handleManualEntry = () => {
    setSearchMode('manual');
    setSetupStep('details');
    setFormData(prev => ({
      ...prev,
      name: '',
      address: '',
      placeId: '',
      phone: '',
      website: '',
      category: '',
      primaryType: '',
      latitude: null,
      longitude: null,
      openingHours: [],
      assistantName: ''
    }));
  };

  const handleFormUpdate = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));

    // Auto-generate assistant name when business name changes
    if (key === 'name' && value) {
      setFormData(prev => ({
        ...prev,
        assistantName: `${value} Assistant`
      }));
    }
  };

  const handleCreateBusiness = async () => {
    setCreating(true);
    setError('');

    try {
      const businessData = {
        name: formData.name,
        email: user.email,
        user_uid: user.uid, 
        // Add Google Places data to the main business object
        place_id: formData.placeId,
        address: formData.address,
        phone: formData.phone,
        website: formData.website,
        latitude: formData.latitude,
        longitude: formData.longitude,
        primary_type: formData.primaryType,
        opening_hours: formData.openingHours,
        search_mode: searchMode,
        settings: {
          businessType: formData.category || formData.customType || 'Other',
          businessDescription: formData.description,
          assistantName: formData.assistantName,
        }
      };

      const response = await api.createBusiness(businessData);

      if (response.data) {
        const business = {
          ...response.data,
          business_id: response.data.id || response.data.business_id,
          place_id: response.data.place_id || formData.placeId,
          ...businessData
        };

        console.log('Created new business with location data:', business);
        setCurrentBusiness(business);
        localStorage.setItem('currentBusiness', JSON.stringify(business));
        
        // Navigate to completion - business setup is now complete
        window.location.href = '/app';
      }
      
    } catch (error) {
      console.error('Create business error:', error);
      if (error.response?.status === 409) {
        setError('A business with this name already exists or you already have a registered business.');
      } else {
        setError('Failed to create business. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  // Updated Google Places step handler - reuse existing data
  const handleGooglePlacesStep = async (includeGooglePlaces) => {
    setCreating(true);
    setError('');

    try {
      if (includeGooglePlaces && formData.placeId && placeDetailsData) {
        // Show the review interface instead of immediately processing
        setEditableGoogleData({
          place_id: formData.placeId,
          name: placeDetailsData.name,
          formatted_address: placeDetailsData.formatted_address,
          formatted_phone_number: placeDetailsData.formatted_phone_number,
          international_phone_number: placeDetailsData.international_phone_number,
          website: placeDetailsData.website,
          types: placeDetailsData.types,
          primary_category: placeDetailsData.primary_category,
          geometry: placeDetailsData.geometry,
          opening_hours: placeDetailsData.opening_hours,
          business_status: placeDetailsData.business_status,
          rating: placeDetailsData.rating,
          user_ratings_total: placeDetailsData.user_ratings_total,
          review_summary: placeDetailsData.review_summary
        });
        
        setShowGooglePlacesReview(true);
        setCreating(false);
        return; // Don't proceed to next step yet
      } else {
        console.log('Skipping Google Places data processing');
        // Navigate to scraping step
        setSetupStep('scraping');
      }
    } catch (error) {
      console.error('Google Places processing error:', error);
      setError(`Failed to process Google Places data: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Add handler for confirming Google Places data
  const handleConfirmGooglePlacesData = async () => {
    setCreating(true);
    setError('');

    try {
      console.log('Processing confirmed Google Places data...');
      
      // Use the correct API method instead of api.post
      const response = await api.processGooglePlaces(user.uid, editableGoogleData);
      
      console.log('Google Places data processed successfully');
      setShowGooglePlacesReview(false);
      setSetupStep('scraping');
      
    } catch (error) {
      console.error('Google Places processing error:', error);
      setError(`Failed to process Google Places data: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Add handler for skipping Google Places after review
  const handleSkipGooglePlacesData = () => {
    setShowGooglePlacesReview(false);
    setSetupStep('scraping');
  };

  // Add handler for updating editable Google data - Fix with useCallback to prevent re-renders
  const handleUpdateGoogleData = useCallback((field, value) => {
    setEditableGoogleData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []); // Empty dependency array means this function never changes

  // Add Google Places Review Component - Fixed with useCallback and better input handling
  const GooglePlacesReviewStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1d2840] mb-2">
          Review Your Google Places Information
        </h2>
        <p className="text-gray-600">
          Please review and edit your business information if needed before adding it to your knowledge base.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-[#1d2840]">Business Information</h3>
          <button
            onClick={() => setIsEditingGoogleData(!isEditingGoogleData)}
            className="flex items-center px-4 py-2 text-[#3DA3DC] border border-[#3DA3DC] rounded-lg hover:bg-[#3DA3DC] hover:text-white transition-colors"
          >
            {isEditingGoogleData ? (
              <>
                <Eye className="w-4 h-4 mr-2" />
                View Mode
              </>
            ) : (
              <>
                <Edit className="w-4 h-4 mr-2" />
                Edit Mode
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Name - Fixed with direct value management */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Name
            </label>
            {isEditingGoogleData ? (
              <input
                type="text"
                defaultValue={editableGoogleData?.name || ''}
                onBlur={(e) => handleUpdateGoogleData('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#3DA3DC] focus:outline-none"
              />
            ) : (
              <p className="p-3 bg-gray-50 rounded-lg">{editableGoogleData?.name || 'N/A'}</p>
            )}
          </div>

          {/* Address - Fixed with direct value management */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            {isEditingGoogleData ? (
              <textarea
                defaultValue={editableGoogleData?.formatted_address || ''}
                onBlur={(e) => handleUpdateGoogleData('formatted_address', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#3DA3DC] focus:outline-none resize-none"
              />
            ) : (
              <p className="p-3 bg-gray-50 rounded-lg">{editableGoogleData?.formatted_address || 'N/A'}</p>
            )}
          </div>

          {/* Phone Number - Fixed with direct value management */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            {isEditingGoogleData ? (
              <input
                type="text"
                defaultValue={editableGoogleData?.formatted_phone_number || ''}
                onBlur={(e) => handleUpdateGoogleData('formatted_phone_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#3DA3DC] focus:outline-none"
              />
            ) : (
              <p className="p-3 bg-gray-50 rounded-lg">{editableGoogleData?.formatted_phone_number || 'N/A'}</p>
            )}
          </div>

          {/* Website - Fixed with direct value management */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            {isEditingGoogleData ? (
              <input
                type="url"
                defaultValue={editableGoogleData?.website || ''}
                onBlur={(e) => handleUpdateGoogleData('website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#3DA3DC] focus:outline-none"
              />
            ) : (
              <p className="p-3 bg-gray-50 rounded-lg">
                {editableGoogleData?.website ? (
                  <a
                    href={editableGoogleData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#3DA3DC] hover:underline"
                  >
                    {editableGoogleData.website}
                  </a>
                ) : (
                  'N/A'
                )}
              </p>
            )}
          </div>

          {/* Business Category - Fixed with direct value management */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Category
            </label>
            {isEditingGoogleData ? (
              <input
                type="text"
                defaultValue={editableGoogleData?.primary_category || ''}
                onBlur={(e) => handleUpdateGoogleData('primary_category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#3DA3DC] focus:outline-none"
              />
            ) : (
              <p className="p-3 bg-gray-50 rounded-lg">{editableGoogleData?.primary_category || 'N/A'}</p>
            )}
          </div>

          {/* Rating - Read only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating & Reviews
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              {editableGoogleData?.rating ? (
                <div className="flex items-center">
                  <span className="text-lg font-semibold">{editableGoogleData.rating}</span>
                  <div className="flex ml-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(editableGoogleData.rating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">
                    ({editableGoogleData.user_ratings_total || 0} reviews)
                  </span>
                </div>
              ) : (
                'No rating available'
              )}
            </div>
          </div>
        </div>

        {/* Opening Hours - Read only */}
        {editableGoogleData?.opening_hours?.weekday_text && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Hours
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              {editableGoogleData.opening_hours.weekday_text.map((hours, index) => (
                <div key={`hours-${index}`} className="text-sm mb-1">
                  {hours}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review Summary - Enhanced with better editing using onBlur */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Customer Review Summary
          </label>
          <div className="p-3 bg-gray-50 rounded-lg">
            {editableGoogleData?.review_summary ? (
              <div>
                <p className="text-sm font-medium mb-2">Review Summary Available:</p>
                {isEditingGoogleData ? (
                  <textarea
                    defaultValue={JSON.stringify(editableGoogleData.review_summary, null, 2)}
                    onBlur={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleUpdateGoogleData('review_summary', parsed);
                      } catch (error) {
                        console.log('Invalid JSON, keeping original value:', error);
                        // Keep original value if JSON is invalid
                        e.target.value = JSON.stringify(editableGoogleData.review_summary, null, 2);
                      }
                    }}
                    rows={8}
                    className="w-full text-sm bg-white p-2 rounded border focus:border-[#3DA3DC] focus:outline-none font-mono"
                    placeholder="Edit the JSON review summary..."
                  />
                ) : (
                  <pre className="text-sm bg-white p-2 rounded border overflow-auto max-h-40">
                    {JSON.stringify(editableGoogleData.review_summary, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  No review summary available from Google Places API
                </p>
                {/* Fallback: Show basic review info */}
                {editableGoogleData?.rating && editableGoogleData?.user_ratings_total && (
                  <div className="p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                    <p className="text-sm">
                      <strong>Available Review Data:</strong><br/>
                      Rating: {editableGoogleData.rating}/5 stars<br/>
                      Total Reviews: {editableGoogleData.user_ratings_total}<br/>
                      <em>Note: Detailed review summary not available from Google Places API</em>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Place ID (read-only) */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Place ID
          </label>
          <div className="p-3 bg-gray-100 rounded-lg">
            <code className="text-sm text-gray-600 break-all">{editableGoogleData?.place_id}</code>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleSkipGooglePlacesData}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Skip Google Places Data
        </button>
        
        <button
          onClick={handleConfirmGooglePlacesData}
          disabled={creating}
          className="px-6 py-3 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all"
        >
          {creating ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
              Processing...
            </div>
          ) : (
            'Confirm & Add to Knowledge Base'
          )}
        </button>
      </div>
    </div>
  );

  // Add Website Content Review Component
  const WebsiteReviewStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1d2840] mb-2">
          Review Scraped Website Content
        </h2>
        <p className="text-gray-600">
          Please review the content extracted from your website before adding it to your knowledge base.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-[#1d2840]">Website Content</h3>
          <button
            onClick={() => setIsEditingWebsiteData(!isEditingWebsiteData)}
            className="flex items-center px-4 py-2 text-[#3DA3DC] border border-[#3DA3DC] rounded-lg hover:bg-[#3DA3DC] hover:text-white transition-colors"
          >
            {isEditingWebsiteData ? (
              <>
                <Eye className="w-4 h-4 mr-2" />
                View Mode
              </>
            ) : (
              <>
                <Edit className="w-4 h-4 mr-2" />
                Edit Mode
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Website URL - Read only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website URL
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <a
                href={editableWebsiteData?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3DA3DC] hover:underline break-all"
              >
                {editableWebsiteData?.url}
              </a>
            </div>
          </div>

          {/* Content Statistics - Simplified to only show content length */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Statistics
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center">
                  <span className="font-medium">Content Length:</span>
                  <span className="ml-2">{editableWebsiteData?.content_length?.toLocaleString() || 0} characters</span>
                </span>
              </div>
            </div>
          </div>

          {/* Content Preview - Editable */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Preview
            </label>
            {isEditingWebsiteData ? (
              <textarea
                defaultValue={editableWebsiteData?.preview || ''}
                onBlur={(e) => handleUpdateWebsiteData('preview', e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#3DA3DC] focus:outline-none resize-none"
                placeholder="Preview of scraped content..."
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg max-h-80 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap break-words">
                  {editableWebsiteData?.preview || 'No preview available'}
                </pre>
              </div>
            )}
          </div>

          {/* Content Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Processing Status
            </label>
            <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-green-700 font-medium">Content Successfully Extracted</span>
              </div>
              <p className="text-green-600 text-sm mt-1">
                The website content has been processed and is ready to be added to your knowledge base.
              </p>
            </div>
          </div>

          {/* Show extracted URLs if available */}
          {Array.isArray(editableWebsiteData?.scraped_urls) && editableWebsiteData.scraped_urls.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pages Extracted (up to 5)
              </label>
              <ul className="list-disc pl-6 text-blue-700 text-sm">
                {editableWebsiteData.scraped_urls.map((url, idx) => (
                  <li key={idx}>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline break-all">
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleSkipWebsiteData}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Skip Website Content
        </button>
        
        <button
          onClick={handleConfirmWebsiteData}
          disabled={creating}
          className="px-6 py-3 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all"
        >
          {creating ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
              Processing...
            </div>
          ) : (
            'Confirm & Continue'
          )}
        </button>
      </div>
    </div>
  );

  const handleWebsiteScraping = async () => {
    if (!formData.website) {
      setError('No website URL available to scrape');
      return;
    }

    setScraping(true);
    setScrapingStep(0);
    setScrapingStatus('');
    setError('');

    try {
      // Simulate quick progress for UI feedback (first 2 steps)
      for (let i = 0; i < 2; i++) {
        setScrapingStep(i);
        await new Promise(res => setTimeout(res, 600));
      }

      // "Extracting content..." step should last at least 10 seconds
      setScrapingStep(2);
      let extractStart = Date.now();
      await new Promise(res => setTimeout(res, 10000));
      let extractElapsed = Date.now() - extractStart;

      // Show "Structuring content..." and then "Finalizing..." as long-running steps
      setScrapingStep(3);
      setScrapingStatus("Structuring  content. This may take up to 2 minutes for large websites...");

      // Call the backend API to scrape the website (real step)
      const response = await api.scrapeWebsite({
        url: formData.website,
        business_id: user.uid
      });

      setScrapingStep(4);
      setScrapingStatus("Finalizing and saving data...");

      if (response.data) {
        setScrapingComplete(true);

        setEditableWebsiteData({
          url: formData.website,
          content: response.data.content_length ? 'Content successfully extracted' : 'No content extracted',
          preview: response.data.preview || 'No preview available',
          content_length: response.data.content_length || 0,
          document_id: response.data.document_id,
          vector_count: response.data.vector_count || 0,
          scraped_urls: response.data.scraped_urls || []
        });

        setShowWebsiteReview(true);
        setScrapingStatus('');
        console.log('Website scraped and ready for review:', response.data);
      }
    } catch (error) {
      console.error('Website scraping error:', error);
      setError('Failed to scrape website. You can skip this step and add content later.');
      setScrapingStatus('');
    } finally {
      setScraping(false);
      setScrapingStep(0);
    }
  };

  const renderScrapingStep = () => (
    <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-center mb-6">
        <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-2" />
        <span className="text-[#1d2840] font-bold text-2xl">Super</span>
        <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 text-[#1d2840]">Populate Knowledge Base</h2>
      <p className="text-gray-600 mb-6">
        Would you like to scrape your website to automatically populate your AI assistant's knowledge base?
      </p>

      {formData.website && (
        <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
          <p className="text-sm text-blue-800">
            <Globe className="w-4 h-4 inline mr-1" />
            Website: {formData.website}
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {scrapingComplete && !showWebsiteReview && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
          ✅ Website content has been successfully scraped and is ready for review!
        </div>
      )}

      {scraping && (
        <div className="mb-4">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#3DA3DC] border-t-transparent mb-2"></div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
              <div
                className="bg-[#3DA3DC] h-2 rounded-full transition-all"
                style={{ width: `${((scrapingStep + 1) / scrapingSteps.length) * 100}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-700 font-medium text-center">
              {scrapingStatus || scrapingSteps[scrapingStep]}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {formData.website && !scrapingComplete ? (
          <button
            onClick={handleWebsiteScraping}
            disabled={scraping}
            className="w-full flex items-center justify-center px-4 py-3 bg-[#3DA3DC] text-white rounded-md hover:bg-[#2b8cb8] transition-colors disabled:opacity-50"
          >
            {scraping ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                Scraping Website...
              </>
            ) : (
              <>
                <Globe className="w-5 h-5 mr-2" />
                Yes, Scrape My Website
              </>
            )}
          </button>
        ) : !formData.website ? (
          <div className="p-4 bg-gray-50 rounded-md text-center">
            <p className="text-gray-600 text-sm">
              No website URL detected. You can add content to your knowledge base later.
            </p>
          </div>
        ) : null}

        <button
          onClick={() => setSetupStep('assistant')}
          disabled={scraping}
          className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          {scrapingComplete ? 'Continue to Assistant Setup' : 'Skip for Now'}
        </button>
      </div>

      <div className="flex justify-between mt-6">
        <button
          type="button"
          onClick={() => setSetupStep('details')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          disabled={scraping}
        >
          Back
        </button>
      </div>

      <p className="mt-4 text-sm text-gray-500 text-center">
        {scrapingComplete 
          ? "Great! Your website content has been processed. You can review and edit it before continuing."
          : "You can always add website content and other documents to your knowledge base later from the main dashboard."
        }
      </p>
    </div>
  );

  const renderAssistantStep = () => (
    <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-center mb-6">
        <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-2" />
        <span className="text-[#1d2840] font-bold text-2xl">Super</span>
        <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 text-[#1d2840]">Customize Your AI Assistant</h2>
      <p className="text-gray-600 mb-6">Give your AI assistant a name that represents your business.</p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleCreateBusiness(); }} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            AI Assistant Name *
          </label>
          <input
            type="text"
            value={formData.assistantName}
            onChange={(e) => handleFormUpdate('assistantName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
            placeholder="e.g., Pizza Palace Assistant"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            This name will be used when your AI assistant interacts with customers.
          </p>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setSetupStep('scraping')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            disabled={creating}
          >
            Back
          </button>
          <button
            type="submit"
            disabled={creating || !formData.assistantName.trim()}
            className="bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] text-white py-3 px-6 rounded-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 flex items-center space-x-2"
          >
            {creating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <span>Create My AI Assistant</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </form>

      <p className="mt-4 text-sm text-gray-500 text-center">
        Your AI assistant will be customized based on your business details.
      </p>
    </div>
  );

  // Render function for search step
  const renderSearchStep = () => (
    <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-center mb-6">
        <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-2" />
        <span className="text-[#1d2840] font-bold text-2xl">Super</span>
        <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 text-[#1d2840]">Find Your Business</h2>
      <p className="text-gray-600 mb-6">Search for your business using Google Places to auto-fill details.</p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search for your business (name or address)..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePlaceSearch(e.target.value);
              }
            }}
            disabled={searching}
          />
          {searching && (
            <div className="absolute right-3 top-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#3DA3DC] border-t-transparent"></div>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            const input = document.querySelector('input[placeholder*="Search for your business"]');
            if (input && input.value.trim()) {
              handlePlaceSearch(input.value);
            }
          }}
          disabled={searching}
          className="w-full flex items-center justify-center px-4 py-3 bg-[#3DA3DC] text-white rounded-md hover:bg-[#2b8cb8] transition-colors disabled:opacity-50"
        >
          <Search className="w-5 h-5 mr-2" />
          {searching ? 'Searching...' : 'Search Business'}
        </button>

        {searchResults.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-md max-h-60 overflow-y-auto">
            <div className="p-3 bg-gray-50 border-b">
              <h3 className="font-medium text-gray-700">Select your business:</h3>
            </div>
            {searchResults.map((place, index) => (
              <div
                key={index}
                className="p-3 border-b hover:bg-gray-50 cursor-pointer"
                onClick={() => handlePlaceSelection(place)}
              >
                <div className="font-medium">{place.name}</div>
                <div className="text-sm text-gray-600">{place.formatted_address}</div>
                {place.primary_category && (
                  <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                    {place.primary_category}
                  </div>
                )}
                {place.rating && (
                  <div className="text-sm text-gray-500">
                    ⭐ {place.rating} ({place.user_ratings_total} reviews)
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center my-4">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-gray-500 text-sm">OR</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        <button
          onClick={handleManualEntry}
          className="w-full flex items-center justify-center px-4 py-3 border border-[#3DA3DC] text-[#3DA3DC] rounded-md hover:bg-[#3DA3DC] hover:text-white transition-colors"
        >
          <Edit3 className="w-5 h-5 mr-2" />
          Enter Business Details Manually
        </button>
      </div>

      <p className="mt-4 text-sm text-gray-500 text-center">
        Can't find your business? Use manual entry to add your details.
      </p>
    </div>
  );

  // Render function for details step
  const renderDetailsStep = () => (
    <div className="max-w-2xl w-full p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-center mb-6">
        <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-2" />
        <span className="text-[#1d2840] font-bold text-2xl">Super</span>
        <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 text-[#1d2840]">
        {searchMode === 'auto' ? 'Verify Business Details' : 'Enter Business Details'}
      </h2>
      
      {searchMode === 'auto' && (
        <p className="text-gray-600 mb-6">
          We found your business! Please verify and edit any details as needed.
        </p>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); setSetupStep('googlePlaces'); }} className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Business Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFormUpdate('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => handleFormUpdate('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleFormUpdate('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Globe className="w-4 h-4 inline mr-1" />
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleFormUpdate('website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Category *
              </label>
              
              {searchMode === 'auto' ? (
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={formData.category}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                      readOnly
                    />
                    <span className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      Auto-detected
                    </span>
                  </div>
                </div>
              ) : (
                <select
                  value={formData.category}
                  onChange={(e) => handleFormUpdate('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
                  required
                >
                  <option value="">Select business category</option>
                  {businessCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              )}
            </div>

            {formData.category === 'Other' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Category
                </label>
                <input
                  type="text"
                  value={formData.customType}
                  onChange={(e) => handleFormUpdate('customType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
                  placeholder="Enter your business category"
                  required
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleFormUpdate('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:outline-none"
                rows={3}
                placeholder="Brief description of your business..."
              />
            </div>
          </div>

          {formData.openingHours.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Opening Hours
              </h4>
              <div className="text-sm text-gray-600 space-y-1">
                {formData.openingHours.map((hours, index) => (
                  <div key={index}>{hours}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setSetupStep('search')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!formData.name || (!formData.category && !formData.customType)}
            className="px-6 py-2 bg-[#3DA3DC] text-white rounded-md hover:bg-[#2b8cb8] disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );

  // Render function for Google Places step
  const renderGooglePlacesStep = () => (
    <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-center mb-6">
        <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-2" />
        <span className="text-[#1d2840] font-bold text-2xl">Super</span>
        <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 text-[#1d2840]">Add Google Places Data</h2>
      <p className="text-gray-600 mb-6">
        Would you like to add your Google Places business information and reviews to help your AI provide better responses?
      </p>

      {formData.placeId && placeDetailsData && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-medium text-blue-900 mb-2">Found Google Places Information:</h3>
          <div className="space-y-1 text-sm text-blue-800">
            <p><strong>Business:</strong> {placeDetailsData.name}</p>
            <p><strong>Address:</strong> {placeDetailsData.formatted_address}</p>
            {placeDetailsData.formatted_phone_number && (
              <p><strong>Phone:</strong> {placeDetailsData.formatted_phone_number}</p>
            )}
            {placeDetailsData.website && (
              <p><strong>Website:</strong> {placeDetailsData.website}</p>
            )}
            <p><strong>Place ID:</strong> <code className="text-xs bg-white px-1 py-0.5 rounded">{formData.placeId}</code></p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {formData.placeId && placeDetailsData ? (
          <>
            <button
              onClick={() => handleGooglePlacesStep(true)}
              disabled={creating}
              className="w-full flex items-center justify-center px-4 py-3 bg-[#3DA3DC] text-white rounded-md hover:bg-[#2b8cb8] transition-colors disabled:opacity-50"
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Globe className="w-5 h-5 mr-2" />
                  Yes, Add Google Places Data
                </>
              )}
            </button>

            <button
              onClick={() => handleGooglePlacesStep(false)}
              disabled={creating}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Skip Google Places Data
            </button>
          </>
        ) : (
          <div className="p-4 bg-gray-50 rounded-md text-center">
            <p className="text-gray-600 text-sm">
              No Google Places data available. You can add this information later from the Knowledge Base section.
            </p>
            <button
              onClick={() => setSetupStep('scraping')}
              className="mt-3 px-4 py-2 bg-[#3DA3DC] text-white rounded-md hover:bg-[#2b8cb8] transition-colors"
            >
              Continue
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <button
          type="button"
          onClick={() => setSetupStep('details')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          disabled={creating}
        >
          Back
        </button>
      </div>

      <p className="mt-4 text-sm text-gray-500 text-center">
        Google Places data includes your business information, hours, and customer reviews to help your AI assistant provide accurate responses.
      </p>
    </div>
  );

  // Add missing handler functions after existing handlers and before the main return statement
  
  // Handler for updating editable website data
  const handleUpdateWebsiteData = useCallback((field, value) => {
    setEditableWebsiteData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handler for skipping website data after review
  const handleSkipWebsiteData = () => {
    setShowWebsiteReview(false);
    setSetupStep('assistant');
  };

  // Handler for confirming website data
  const handleConfirmWebsiteData = async () => {
    setCreating(true);
    setError('');

    try {
      console.log('Confirming website content and indexing...');
      
      // Call the new confirm endpoint to actually index the content
      const response = await api.confirmWebsiteContent(user.uid, {
        document_id: editableWebsiteData?.document_id
      });
      
      console.log('Website content confirmed and indexed:', response.data);
      setShowWebsiteReview(false);
      setSetupStep('assistant');
    } catch (error) {
      console.error('Error confirming website data:', error);
      setError('Failed to confirm and index website data');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-white p-4">
      {/* Show website review interface when reviewing website content */}
      {showWebsiteReview ? (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-3" />
                <div>
                  <span className="text-[#1d2840] font-bold text-2xl">Super</span>
                  <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
                </div>
              </div>
              <div className="w-24 h-1 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] mx-auto rounded"></div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="max-w-4xl mx-auto mb-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <p className="mt-1 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <WebsiteReviewStep />
          </div>
        </div>
      ) : showGooglePlacesReview ? (
        // Show ONLY the Google Places review interface when reviewing Google Places data
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <img src={owlLogo} alt="SuperOwl Logo" className="h-12 mr-3" />
                <div>
                  <span className="text-[#1d2840] font-bold text-2xl">Super</span>
                  <span className="text-[#3DA3DC] font-bold text-2xl">Owl</span>
                </div>
              </div>
              <div className="w-24 h-1 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] mx-auto rounded"></div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="max-w-4xl mx-auto mb-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <p className="mt-1 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <GooglePlacesReviewStep />
          </div>
        </div>
      ) : (
        // Show normal setup steps when not reviewing any data
        <>
          {setupStep === 'search' && renderSearchStep()}
          {setupStep === 'details' && renderDetailsStep()}
          {setupStep === 'googlePlaces' && renderGooglePlacesStep()}
          {setupStep === 'scraping' && renderScrapingStep()}
          {setupStep === 'assistant' && renderAssistantStep()}
        </>
      )}
    </div>
  );
};

export default BusinessSetup;
